import { ZoomIn } from 'lucide-react';
import { CALENDAR_KINDS, WEEKDAYS_AR, WEEKDAYS_SHORT_AR, kindsOfDay, monthGrid, monthKeyOf, parseDayKey, todayKey, type CalendarItem } from '@/lib/calendar';

/**
 * شبكةُ الشهر: مربّعاتٌ بتواريخ الأيام، وتحت كل رقمٍ نقطةٌ بلون كل نوعٍ محجوزٍ في اليوم
 * (جلسة · إرشاد · حصة · استشارة) — فيُرى من الخارج أن اليومَ مشغول وبماذا، قبل أي ضغطة.
 * الصفُّ أسبوعٌ يبدأ الأحد، وأولُ خانةٍ فيه زرُّ «تركيز» يكبّر الأسبوعَ إلى عرض الساعات (`onZoomWeek`).
 *
 * `compact` لمنتقي التاريخ: خاناتٌ أصغر ونقاطٌ بلا أعداد. `min/max` بمفاتيح أيامٍ يُعطَّل ما خرج عنهما.
 */
interface Props {
  monthKey: string;
  byDay: Map<string, CalendarItem[]>;
  selected?: string | null;
  onPickDay?: (dayKey: string) => void;
  onZoomWeek?: (weekStart: string) => void;
  min?: string;
  max?: string;
  compact?: boolean;
  /** يُعرض بدل الرقم للأيام التي تحمله — رقمٌ أو نصٌّ قصير */
  idPrefix?: string;
  /** العطلُ: مفتاحُ اليوم ← اسمُها (0084) — يُظلَّل اليومُ ويُسمّى */
  holidays?: Map<string, string>;
}

export function MonthGrid({ monthKey, byDay, selected, onPickDay, onZoomWeek, min, max, compact, idPrefix = 'mg', holidays }: Props) {
  const weeks = monthGrid(monthKey);
  const today = todayKey();
  const names = compact ? WEEKDAYS_SHORT_AR : WEEKDAYS_AR;
  return (
    <div className={`mgrid${compact ? ' mgrid--compact' : ''}${onZoomWeek ? ' mgrid--zoomable' : ''}`} role="grid" aria-label="أيام الشهر">
      <div className="mgrid__row mgrid__row--head" role="row">
        {onZoomWeek && <span className="mgrid__zoomcell" aria-hidden />}
        {names.map((n, i) => <span key={n} className="mgrid__dow" role="columnheader" aria-label={WEEKDAYS_AR[i]}>{n}</span>)}
      </div>
      {weeks.map((week) => (
        <div key={week[0]} className="mgrid__row" role="row">
          {onZoomWeek && (
            <button type="button" className="mgrid__zoom" onClick={() => onZoomWeek(week[0])} title="ركّز على هذا الأسبوع" aria-label={`ركّز على أسبوع ${parseDayKey(week[0]).d}/${parseDayKey(week[0]).m}`}>
              <ZoomIn size={15} aria-hidden />
            </button>
          )}
          {week.map((day) => {
            const items = byDay.get(day);
            const kinds = kindsOfDay(items);
            const inMonth = monthKeyOf(day) === monthKey;
            const disabled = Boolean((min && day < min) || (max && day > max));
            const n = parseDayKey(day).d;
            const holiday = holidays?.get(day);
            const cls = ['mgrid__day', inMonth ? '' : 'mgrid__day--out', day === today ? 'mgrid__day--today' : '', day === selected ? 'mgrid__day--selected' : '', items?.length ? 'mgrid__day--busy' : '', holiday ? 'mgrid__day--holiday' : ''].filter(Boolean).join(' ');
            const label = `${n} — ${holiday ? `عطلة: ${holiday} — ` : ''}${items?.length ? `${items.length} موعد` : 'لا مواعيد'}${day === today ? ' (اليوم)' : ''}`;
            return (
              <button
                key={day} type="button" role="gridcell" id={`${idPrefix}-${day}`} className={cls} disabled={disabled}
                aria-selected={day === selected || undefined} aria-current={day === today ? 'date' : undefined} aria-label={label}
                data-day={day} onClick={() => onPickDay?.(day)} title={holiday}
              >
                <span className="mgrid__num">{n}</span>
                {holiday && !compact && <span className="mgrid__holiday" aria-hidden>{holiday}</span>}
                {kinds.length > 0 && (
                  <span className="mgrid__dots" aria-hidden>
                    {kinds.map((k) => <i key={k} className={`mgrid__dot ${CALENDAR_KINDS[k].className}`} />)}
                  </span>
                )}
                {!compact && items && items.length > 0 && <span className="mgrid__count" aria-hidden>{items.length}</span>}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
